import type { GroupBase } from 'react-select';
import type { ReneoSelectOption } from './ReneoSelect';

export function getOptionLabel<TOption extends ReneoSelectOption>(opt: TOption, labelKey: keyof TOption): string {
    const val = opt[labelKey];
    return typeof val === 'string' ? val : String(val ?? '');
}

export function getOptionValue<TOption extends ReneoSelectOption>(opt: TOption, valueKey: keyof TOption): string {
    const val = opt[valueKey];
    return typeof val === 'string' ? val : String(val ?? '');
}

export function flattenOptions<TOption extends ReneoSelectOption>(options: readonly TOption[] | readonly GroupBase<TOption>[] | undefined): TOption[] {
    if (!options) return [];
    const opts = options as TOption[] | GroupBase<TOption>[];
    return opts.flatMap(o => ('options' in o ? (o as GroupBase<TOption>).options : [o]));
}

/** Only label/value keys are populated — extra TOption fields will be missing at runtime. */
function createFallbackOption<TOption extends ReneoSelectOption>(value: string, labelKey: keyof TOption, valueKey: keyof TOption): TOption {
    return { [labelKey]: value, [valueKey]: value } as unknown as TOption;
}

export function resolveMultiValue<TOption extends ReneoSelectOption>(
    valueProp: TOption[] | Array<string | number> | undefined,
    flatOptions: TOption[],
    labelKey: keyof TOption,
    valueKey: keyof TOption,
): TOption[] {
    // Empty must be `[]`, not `undefined` — otherwise react-select goes uncontrolled and
    // "Select none" fails to clear options that were toggled manually.
    if (!valueProp?.length) return [];

    const first = valueProp[0];
    if (typeof first === 'object' && first !== null && valueKey in first) {
        return valueProp as TOption[];
    }

    return (valueProp as Array<string | number>).map(val => {
        const key = String(val);
        return flatOptions.find(o => getOptionValue(o, valueKey) === key) ?? createFallbackOption<TOption>(key, labelKey, valueKey);
    });
}

export function resolveSingleValue<TOption extends ReneoSelectOption>(
    valueProp: TOption | string | number | null | undefined,
    flatOptions: TOption[],
    labelKey: keyof TOption,
    valueKey: keyof TOption,
): TOption | null {
    if (valueProp == null || valueProp === '') return null;

    if (typeof valueProp === 'object' && valueKey in valueProp) {
        return valueProp as TOption;
    }

    const key = String(valueProp);
    const found = flatOptions.find(o => getOptionValue(o, valueKey) === key);
    return found ?? createFallbackOption<TOption>(key, labelKey, valueKey);
}

export function handleMultiChange<TOption extends ReneoSelectOption>(selected: TOption[], emitObject: boolean, valueKey: keyof TOption): TOption[] | string[] {
    if (selected.length === 0) return [];
    return emitObject ? selected : selected.map(o => getOptionValue(o, valueKey));
}

export function handleSingleChange<TOption extends ReneoSelectOption>(
    selected: TOption | null,
    emitObject: boolean,
    valueKey: keyof TOption,
): TOption | string | undefined {
    if (!selected) return undefined;
    return emitObject ? selected : getOptionValue(selected, valueKey);
}

export function isOptionDisabled<TOption extends ReneoSelectOption>(
    option: TOption,
    resolvedValue: TOption[] | undefined,
    maxSelections: number | undefined,
    valueKey: keyof TOption,
): boolean {
    if (!maxSelections) return false;

    const currentCount = resolvedValue?.length ?? 0;
    if (currentCount < maxSelections) return false;

    const optionValue = getOptionValue(option, valueKey);
    return !resolvedValue?.some(o => getOptionValue(o, valueKey) === optionValue);
}
